/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../../ProposalDAOImpl';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { ComparisonItemDAO } from '../../ProposalItemDAO';
import { DataPoints, DataPointResponse, ESGOverviewDetails } from '../../DataPoints';
import { ESGRatingDistributionValue } from '../../../../../enums/Esg';
import { PercentageCell } from '../../../../../views/components/cells/index';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import { ProposalStyleClassNames } from '../../../ProposalStyleClassNames';

// Utils
import { deepClone } from '../../../../../utils/objects';
import { formatMessageWithLocale, formatOrdinalSuffix } from '../../../../../utils/i18n';
import DonutData from '@/types/donut-data';
import ESGAnalytics from '@/types/esg-analytics';

/**
 * @class ESG Overview Data Access Object
 */
export default class ESGOverviewDAO extends ComparisonItemBasedProposalDAO {
  groupByObj: ProposalNS.GroupByESGOverviewDAOOptions = {
    totalPosition: ProposalNS.DAOGroupByTotalPosition.BOTTOM,
    order: [
      {
        groupKey: ESGRatingDistributionValue.AAA,
        keys: [ESGRatingDistributionValue.AAA],
      },
      {
        groupKey: ESGRatingDistributionValue.AA,
        keys: [ESGRatingDistributionValue.AA],
      },
      {
        groupKey: ESGRatingDistributionValue.A,
        keys: [ESGRatingDistributionValue.A],
      },
      {
        groupKey: ESGRatingDistributionValue.BBB,
        keys: [ESGRatingDistributionValue.BBB],
      },
      {
        groupKey: ESGRatingDistributionValue.BB,
        keys: [ESGRatingDistributionValue.BB],
      },
      {
        groupKey: ESGRatingDistributionValue.B,
        keys: [ESGRatingDistributionValue.B],
      },
      {
        groupKey: ESGRatingDistributionValue.CCC,
        keys: [ESGRatingDistributionValue.CCC],
      },
      {
        groupKey: ESGRatingDistributionValue.NO_VALUE,
        keys: [ESGRatingDistributionValue.NO_VALUE],
      },
    ],
  };

  out: DonutData | null = null;

  defaultGroupByObj: ProposalNS.GroupByESGOverviewDAOOptions = this.groupByObj;

  esgData: ESGAnalytics | undefined = undefined;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new ESGOverviewDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['ESG_OVERVIEW_COLORS'];
  }

  protected build(): void {
    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      this.groupByObj = deepClone(this.defaultGroupByObj);
      this.esgData = itemDAO.item.esg;

      // it should NEVER be undefined - assigned at construction, only reassigned if not undefined.
      if (this.groupByObj.order !== undefined) {
        for (const group of this.groupByObj.order) {
          for (const key of group.keys) {
            this.addOverviewItemField(itemDAO, key);
          }
        }

        this.esgData = undefined;
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    this.out = super.getDonutData(index);

    if (!this.out) {
      return this.out;
    }

    const { configOptions } = this.proposalElement.dataView as ProposalNS.DonutViewOptions;

    if (configOptions.title) {
      const item: ComparisonItemDAO = this.getItemDAOs()[index];
      const { response }: DataPointResponse<ESGOverviewDetails> = DataPoints.getESGOverviewData(item.item);

      this.out.title = {
        ...configOptions.title,
        text: configOptions.title.text
          .replaceAll(
            '{{value}}',
            !response.value
              ? this.formatMessage('Common.NotApplicable')
              : response.value === 'NoValue'
                ? this.formatMessage('ESGComparisonTool.Rating.NoData')
                : response.value,
          )
          .replace(
            '{{classification}}',
            response.classification ? this.formatMessage(`ESGComparisonTool.Rating.Classification.${response.classification}`) : '',
          )
          .replace(
            '{{peerRank}}',
            response.peerRank || response.peerRank === 0
              ? `
                  <h3>${this.formatMessage('ESGComparisonTool.Ranking.PeerRank')}</h3>
                  <p class="classification">
                    <strong>${response.peerRank}</strong>${formatOrdinalSuffix(response.peerRank, this.locale)}
                  </p>
                  <hr />
                  <p className="classification">100</p>
                `
              : '',
          )
          .replace(
            '{{globalRank}}',
            response.globalRank || response.globalRank === 0
              ? `
                  <h3>${this.formatMessage('ESGComparisonTool.Ranking.GlobalRank')}</h3>
                  <p class="classification">
                    <strong>${response.globalRank}</strong>${formatOrdinalSuffix(response.globalRank, this.locale)}
                  </p>
                  <hr />
                  <p className="classification">100</p>
                `
              : '',
          ),
      };
    }

    if (this.colorsDict) {
      this.out.colorsDict = this.colorsDict;
    }

    return this.out;
  }

  // private methods

  private addOverviewItemField(itemDAO: ComparisonItemDAO, key: string): number {
    const fieldName: string = key === 'NoValue' ? formatMessageWithLocale('ESGComparisonTool.Rating.NoData', this.locale) : key;
    const assetAllocation = DataPoints.getESGRatingAllocationByKey(itemDAO.item, key);
    this.addDisclaimerKeys(assetAllocation.disclaimerKeys, itemDAO.item);
    const allocationValue = assetAllocation.response;
    let color: string | undefined;

    if (this.colorsDict) {
      color = this.colorsDict[key];
    }

    const field = new ProposalFieldDAO(fieldName, allocationValue ? allocationValue.toFixed(2) : 0, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = PercentageCell;
    field.key = key;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableContent, color);

    return assetAllocation.response;
  }
}
